/**
 * Vista di un tag — `/tags/:tag` (F-080).
 *
 * Ci si arriva cliccando un tag da qualunque punto in cui compaia. Mostra tutti
 * i titoli che lo portano, ciascuno con **tutti** i suoi tag e non solo quello
 * cercato: è il contesto che rende l'elenco leggibile — vedere che accanto a
 * «usa» c'è anche «ai» dice perché quel titolo è lì.
 *
 * Il tag mostrato in intestazione è quello **normalizzato** restituito dal
 * server, non quello scritto nell'URL: chi arriva da un link con «USA» deve
 * vedere «usa» sopra un elenco costruito su «usa», altrimenti sembrerebbero due
 * cose diverse.
 */

import { Link, useParams } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { Badge, Card, EmptyState, Spinner } from '@/ui';
import { useTagView } from './api';
import { TagList } from './TagList';
import './tags.css';

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'Errore imprevisto';
}

export function TagPage() {
  const { tag = '' } = useParams<{ tag: string }>();
  const view = useTagView(tag);

  if (view.isLoading) return <Spinner label="Caricamento dei titoli…" />;
  if (view.isError) {
    return (
      <EmptyState
        icon="🏷️"
        title="Tag non disponibile"
        description={errorMessage(view.error)}
        action={
          <Link className="btn btn--secondary" to="/watchlists">
            Vai alla watchlist
          </Link>
        }
      />
    );
  }
  if (!view.data) return null;

  const { tag: normalized, items, total } = view.data;

  return (
    <div className="tag-page">
      <nav className="tag-page__breadcrumb" aria-label="Percorso">
        <Link to="/watchlists">Watchlist</Link>
        <span aria-hidden="true"> / </span>
        <span>Tag</span>
      </nav>

      <header className="tag-page__header">
        <h1>
          <span className="tag tag--title">{normalized}</span>
        </h1>
        <p className="tag-page__count">
          {total === 0 ? 'Nessun titolo con questo tag' : `${total} ${total === 1 ? 'titolo' : 'titoli'}`}
        </p>
      </header>

      {items.length === 0 ? (
        <EmptyState
          icon="🏷️"
          title="Nessun titolo con questo tag"
          description="Il tag non è (o non è più) assegnato ad alcun titolo. I tag si aggiungono dalla pagina di dettaglio del titolo."
        />
      ) : (
        <Card flush>
          <ul className="tag-page__list">
            {items.map((item) => (
              <li key={item.instrumentId}>
                <div className="tag-page__item-head">
                  <Link to={`/instruments/${item.instrumentId}`}>
                    <strong>{item.name}</strong>
                  </Link>
                  {item.symbol && <Badge tone="accent">{item.symbol}</Badge>}
                  <span className="tag-page__meta numeric">
                    {item.isin ?? 'Senza ISIN'}
                    {item.marketMic && <> · {item.marketMic}</>}
                    <> · {item.currency}</>
                  </span>
                </div>
                {/* Tutti i suoi tag, quello cercato compreso: toglierlo
                    dall'elenco farebbe sparire proprio l'informazione che
                    spiega perché il titolo è in questa pagina. */}
                <TagList tags={item.tags} size="small" />
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
